import { useCallback, useMemo } from 'react';

import { readEml, getEmailAddress } from 'eml-parse-js';

import useFetch from '../../hooks/useFetch';

export interface EmailAddress {
  name?: string;
  email: string;
}

export interface ParsedAttachment {
  name: string;
  contentType: string;
  data64: string;
  cid?: string;
  inline: boolean;
}

/**
 * Raw result from eml-parse-js library.
 * This interface documents the expected shape of parsed EML data.
 */
export interface EmlParsedResult {
  subject?: string;
  from?: EmailAddress | EmailAddress[];
  to?: EmailAddress | EmailAddress[];
  cc?: EmailAddress | EmailAddress[];
  date?: Date | string;
  html?: string;
  text?: string;
  headers?: {
    'Reply-To'?: string;
    [key: string]: string | undefined;
  };
  attachments?: Array<{
    name?: string;
    filename?: string;
    contentType?: string;
    mimeType?: string;
    data64?: string;
    cid?: string;
    id?: string;
    inline?: boolean;
  }>;
}

export interface ParsedEmail {
  subject: string;
  from: EmailAddress | EmailAddress[] | null;
  to: EmailAddress | EmailAddress[] | null;
  cc?: EmailAddress | EmailAddress[] | null;
  replyTo?: EmailAddress | EmailAddress[] | null;
  date: Date | string;
  html?: string;
  text?: string;
  attachments: ParsedAttachment[];
}

interface UseEmlParserResult {
  email: ParsedEmail | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Escapes special regex characters in a string for safe use in RegExp
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Replaces CID references in HTML with data URLs from attachments
 */
const resolveCidReferences = (html: string, attachments: ParsedAttachment[]): string => {
  let resolvedHtml = html;

  attachments.forEach((attachment) => {
    if (attachment.cid && attachment.data64) {
      // Remove angle brackets from CID and escape regex special characters
      const cleanCid = escapeRegex(attachment.cid.replace(/[<>]/g, ''));

      // Skip if CID is empty after cleaning (e.g., was just "<>")
      if (!cleanCid.trim()) {
        return;
      }

      // Match both cid:xxx and cid:<xxx> formats (some email clients include angle brackets in HTML)
      const cidPattern = new RegExp(`cid:(?:<)?${cleanCid}(?:>)?`, 'gi');
      // Extract just the base MIME type (e.g., "image/png" from "image/png; name=...")
      // The contentType may include parameters with newlines that break data URLs
      const mimeType = (attachment.contentType || 'application/octet-stream').split(';')[0].trim();
      const dataUrl = `data:${mimeType};base64,${attachment.data64}`;
      resolvedHtml = resolvedHtml.replace(cidPattern, dataUrl);
    }
  });

  return resolvedHtml;
};

/**
 * Wraps the callback-based readEml function in a Promise
 */
const parseEmlContent = (emlContent: string): Promise<EmlParsedResult> => {
  return new Promise((resolve, reject) => {
    readEml(emlContent, (err, parsed) => {
      if (err) {
        reject(new Error(`Failed to parse EML: ${err.message || 'Unknown error'}`));
        return;
      }
      if (!parsed) {
        reject(new Error('Failed to parse EML: No data returned'));
        return;
      }
      resolve(parsed as EmlParsedResult);
    });
  });
};

/**
 * Transforms raw parsed EML result into our ParsedEmail format
 */
const transformParsedEmail = (parsed: EmlParsedResult): ParsedEmail => {
  const attachments: ParsedAttachment[] = (parsed.attachments || []).map((att) => ({
    name: att.name || att.filename || 'attachment',
    contentType: att.contentType || att.mimeType || 'application/octet-stream',
    data64: att.data64 || '',
    cid: att.cid || att.id,
    inline: att.inline || false,
  }));

  let htmlContent = parsed.html;
  if (htmlContent && attachments.length > 0) {
    htmlContent = resolveCidReferences(htmlContent, attachments);
  }

  // Extract Reply-To from headers if present
  const replyToHeader = parsed.headers?.['Reply-To'];
  const replyTo = replyToHeader ? getEmailAddress(replyToHeader) : null;

  return {
    subject: parsed.subject || 'No Subject',
    from: parsed.from ?? null,
    to: parsed.to ?? null,
    cc: parsed.cc,
    replyTo,
    date: parsed.date ?? new Date(),
    html: htmlContent,
    text: parsed.text,
    attachments: attachments.filter((att) => !att.inline),
  };
};

/**
 * Fetches and parses an EML file from a URL
 */
const fetchAndParseEml = async(url: string): Promise<ParsedEmail> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch EML file: ${response.status} ${response.statusText}`);
  }

  const emlContent = await response.text();
  const parsed = await parseEmlContent(emlContent);
  return transformParsedEmail(parsed);
};

/**
 * Hook to fetch and parse EML files.
 * Uses the generic useFetch hook for loading/error state management.
 */
export const useEmlParser = (emlUrl: string | null): UseEmlParserResult => {
  const fetchFunction = useCallback(() => {
    if (!emlUrl) {
      return Promise.reject(new Error('No EML URL provided'));
    }
    return fetchAndParseEml(emlUrl);
  }, [emlUrl]);

  const { data, isLoading, error, refetch } = useFetch<ParsedEmail>(fetchFunction, [emlUrl]);

  // Don't show loading state when URL is null
  const loading = useMemo(() => {
    return emlUrl ? isLoading : false;
  }, [emlUrl, isLoading]);

  return {
    email: data,
    loading,
    error,
    refetch,
  };
};
